import seoService from "~/services/SeoService";

const SITE_URL = "https://www.digitalife.com.mx";
const HOME_META_DESCRIPTION =
  "Compra PC, Mac, laptops, monitores y accesorios en Digitalife México. Equipos gamer y profesionales con envíos rápidos, garantía y atención especializada.";
const HOME_DESCRIPTION =
  "En Digitalife somos una tienda de tecnología en México especializada en la venta de PC, Mac, computadoras gamer, computadoras para diseñadores, youtuber, laptops, monitores, componentes PC gamer y accesorios de cómputo como teclados, mouse, audífonos, bocinas e impresoras al mejor precio. Contamos con un amplio catálogo para quienes buscan comprar hardware en México, desde equipos de alto rendimiento hasta laptops baratas en México con excelente relación calidad-precio. Trabajamos con las mejores marcas del mercado para ofrecer soluciones ideales para gamers, creadores de contenido y uso profesional. Ya sea que quieras armar tu setup, mejorar tu rendimiento o encontrar los mejores monitores gaming a buen precio, en Digitalife encontrarás todo en un solo lugar, con envíos rápidos a todo México, garantía y atención especializada. Contamos con tiendas físicas en Guadalajara, Zapopan, Tlaquepaque, Tlajomulco y todo Jalisco, garantizando que compras en una empresa confiable y segura.";
const STORES_SEO_ENDPOINT = `${import.meta.env.VITE_API_ENDPOINT}/seo/stores`;
const STORES_SEO_CACHE_ENDPOINT = "/api/seo/stores";

interface SeoStore {
  id: number | string;
  name: string;
  slug?: string | null;
  phone?: string | null;
  formatted_address?: string | null;
  street_name?: string | null;
  street_number?: string | null;
  interior_number?: string | null;
  region?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zip?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  opens?: string | null;
  closes?: string | null;
  days?: string[] | null;
  url?: string | null;
}

const formatStoreAddress = (store: SeoStore) => {
  if (store.formatted_address) {
    return store.formatted_address;
  }

  const street = [store.street_name, store.street_number]
    .filter(Boolean)
    .join(" ")
    .trim();
  const interior = store.interior_number
    ? ` (int. ${store.interior_number})`
    : "";
  const zipCity = [store.zip, store.city].filter(Boolean).join(" ");

  return [
    street ? `${street}${interior}` : "",
    store.region,
    zipCity,
    store.state,
  ]
    .filter(Boolean)
    .join(", ");
};

export const useHomeSeo = () => {
  const { data: seoStores } = useAsyncData("home-seo-stores", async () => {
    try {
      return await $fetch<SeoStore[]>(STORES_SEO_CACHE_ENDPOINT);
    } catch {
      return [];
    }
  });

  const storeEntities = computed(() => {
    return (seoStores.value || []).map((store) => {
      const sameUrl =
        store.url || `${SITE_URL}/sucursales/${store.slug || store.id}`;
      const address = {
        "@type": "PostalAddress",
        streetAddress: formatStoreAddress(store),
        addressLocality: store.city || undefined,
        addressRegion: store.state || undefined,
        postalCode: store.zip || undefined,
        addressCountry: store.country || "MX",
      };

      return {
        "@type": ["ComputerStore", "LocalBusiness"],
        "@id": `${sameUrl}#store`,
        name: store.name,
        url: sameUrl,
        telephone: store.phone || undefined,
        address,
        branchOf: { "@id": `${SITE_URL}#organization` },
        openingHoursSpecification:
          store.opens && store.closes
            ? [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: store.days || [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                  ],
                  opens: store.opens,
                  closes: store.closes,
                },
              ]
            : undefined,
        geo:
          store.latitude && store.longitude
            ? {
                "@type": "GeoCoordinates",
                latitude: Number(store.latitude),
                longitude: Number(store.longitude),
              }
            : undefined,
      };
    });
  });

  const jsonLdGraph = computed(() => ({
    "@context": "https://schema.org",
    "@graph": [
      ...storeEntities.value,
      {
        "@type": "Organization",
        "@id": `${SITE_URL}#organization`,
        name: "Digitalife",
        url: SITE_URL,
        logo: `${SITE_URL}/images/logo-square.jpg`,
        description: HOME_DESCRIPTION,
        areaServed: [
          "MX",
          "Jalisco",
          "Guadalajara",
          "Zapopan",
          "Tlaquepaque",
          "Tlajomulco",
        ],
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}#website`,
        url: SITE_URL,
        name: "Digitalife",
        publisher: { "@id": `${SITE_URL}#organization` },
        inLanguage: "es-MX",
        description: HOME_META_DESCRIPTION,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/buscar?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "WebPage",
        "@id": `${SITE_URL}/#webpage`,
        url: `${SITE_URL}/`,
        name: "Inicio | Digitalife",
        inLanguage: "es-MX",
        isPartOf: { "@id": `${SITE_URL}#website` },
        about: { "@id": `${SITE_URL}#organization` },
      },
      {
        "@type": ["ComputerStore", "LocalBusiness"],
        "@id": `${SITE_URL}#store-main`,
        name: "Digitalife",
        url: SITE_URL,
        mainEntityOfPage: { "@id": `${SITE_URL}/#webpage` },
        image: `${SITE_URL}/images/logo-square.jpg`,
        description: HOME_DESCRIPTION,
        areaServed: [
          { "@type": "City", name: "Guadalajara" },
          { "@type": "City", name: "Zapopan" },
          { "@type": "City", name: "Tlaquepaque" },
          { "@type": "City", name: "Tlajomulco" },
          { "@type": "State", name: "Jalisco" },
          { "@type": "Country", name: "México" },
        ],
        hasPOS: storeEntities.value.map((s) => ({ "@id": s["@id"] })),
      },
    ],
  }));

  seoService.setData({
    title: "Todo en Computadoras - Digitalife",
    url: `${SITE_URL}/`,
    description: HOME_META_DESCRIPTION,
  });

  useHead(() => ({
    script: [
      {
        type: "application/ld+json",
        textContent: JSON.stringify(jsonLdGraph.value),
      },
    ],
  }));

  return {
    homeDescription: HOME_DESCRIPTION,
    homeMetaDescription: HOME_META_DESCRIPTION,
    storesSeoEndpoint: STORES_SEO_ENDPOINT,
  };
};
